import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Calendar,
  CheckCircle2,
  Copy,
  CreditCard,
  FileText,
  IndianRupee,
  Info,
  Link2,
  Mail,
  MessageCircle,
  Search,
  UserRound,
  Wifi,
  X,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { useBranding } from "../../contexts/BrandingContext";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import UploadWithS3 from "../Common/UploadWithS3";
import useRazorpay from "../../hooks/useRazorpay";
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardTitle,
  AppModal,
  Callout,
  DetailRow,
  DetailSection,
  EmptyState,
  FormActionBar,
  FormField,
  IconButton,
  Input,
  NativeSelect,
  PageHeader,
  PanelButton,
  ResetButton,
  StatusBadge,
  Textarea,
  UserAvatar,
  cn,
} from "../ui";
import FeePlanModal from "./FeePlanModal";

/** The picked panel — same treatment `GratsMeetSettings` uses for its mode picker.
 *  Not a `PanelButton` prop: the component owns its geometry, the call site owns
 *  what "selected" means. */
const SELECTED_PANEL = "tw-border-primary tw-bg-primary-soft";

const MANUAL_MODES = ["UPI", "Cash", "Bank Transfer", "Card", "Cheque"];
const RAZORPAY_MODE = "Razorpay (Online)";
const LINK_MODE = "Send Payment Link to Student";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "—";

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const daysUntil = (d) => Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);

/**
 * How one installment reads on screen.
 *
 * ⚠️ The urgency is re-derived from `dueDate` rather than trusted from
 * `installments[].status`. That field is refreshed by the 09:30 reminder cron,
 * so between runs a genuinely overdue EMI still says `due` — and a part-paid one
 * is stored as `partial`, which says nothing about whether it is late.
 */
const emiView = (inst) => {
  const amount = Number(inst.amount) || 0;
  const paidAmount = Number(inst.paidAmount) || 0;
  const balance = Math.max(amount - paidAmount, 0);

  if (inst.status === "paid") return { status: "paid", balance, paidAmount, settled: true };
  if (inst.status === "waived") return { status: "waived", balance, paidAmount, settled: true };

  const days = daysUntil(inst.dueDate);
  const status = days < 0 ? "overdue" : days <= 7 ? "due" : "upcoming";
  const relative =
    days < 0
      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`
      : days === 0
        ? "Due today"
        : `in ${days} day${days === 1 ? "" : "s"}`;

  return { status, balance, paidAmount, settled: false, relative, late: days <= 0 };
};

/** An enrollment's fee position, for `domain="feeBalance"`. */
const balanceStatus = (paid, pending) =>
  pending <= 0 ? "paid" : paid > 0 ? "partial" : "pending";

const todayISO = () => new Date().toISOString().slice(0, 10);

const blankForm = () => ({
  currentPayment: "",
  paymentMode: "UPI",
  referenceNumber: "",
  paidTo: "",
  receivedBy: "",
  date: todayISO(),
  refImage: null,
  nextPaymentDate: "",
  comments: "",
});

/**
 * CollectFeePage — record one payment against a student's enrollment.
 *
 * Mounted at both `/fees/new` (search for the student) and
 * `/students/:id/fees/new` (student preselected).
 *
 * Tailwind only, and everything with a name comes from `ui/`. The page it
 * replaces was Tailwind too, but written as ~40 module-scope class strings —
 * `CARD`, `SCARD`, `INPUT`, `SELECT`, `MONEY`, `BTN_PRIMARY` — which is a second
 * copy of the card, the control and the button spec living on one page, and the
 * thing `lint:components --check classconst` counts.
 */
const CollectFeePage = () => {
  const { id: routeStudentId } = useParams();
  const navigate = useNavigate();
  const { openCheckout, loading: rzpLoading } = useRazorpay();
  const branding = useBranding();

  // Student
  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  // Enrollment
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Plan / installments
  const [feePlan, setFeePlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Payment
  const [payChannel, setPayChannel] = useState("manual");
  const [form, setForm] = useState(blankForm);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rzpProcessing, setRzpProcessing] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Payment-link flow
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [sentLink, setSentLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [staff, setStaff] = useState([]);
  const [sendingWa, setSendingWa] = useState(false);

  // Names for the two toggle-button groups below.
  const groupId = React.useId();
  const channelLabelId = `${groupId}-channel`;
  const onlineLabelId = `${groupId}-online`;

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  /* ── mount: gateway config, staff list, who is collecting ── */
  useEffect(() => {
    apiClient.get("/payments/config").then(({ data }) => setPaymentConfig(data)).catch(() => {});
    apiClient
      .get("/admins/list")
      .then(({ data }) =>
        setStaff(
          (data.admins || [])
            .map((a) => a.name || [a.firstName, a.lastName].filter(Boolean).join(" "))
            .filter(Boolean)
        )
      )
      .catch(() => {});
    const me = getCurrentUser();
    const myName = me?.name || [me?.firstName, me?.lastName].filter(Boolean).join(" ");
    if (myName) setForm((p) => ({ ...p, receivedBy: myName }));
  }, []);

  /* ── close the search menu on an outside click ── */
  useEffect(() => {
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setStudentResults([]);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const loadStudent = useCallback(
    async (sid) => {
      try {
        const { data } = await apiClient.get(`/students/${sid}`);
        setSelectedStudent(data.student);
        setLinkEmail(data.student?.email || "");
        setForm((p) => ({ ...p, paidTo: p.paidTo || branding?.brandName || "" }));
        const { data: caData } = await apiClient.get(`/course-assignments/student/${sid}`);
        const active = (caData.assignments || []).filter((a) => a.status === "active");
        setCourseAssignments(active);
        if (active.length >= 1) setSelectedAssignment(active[0]);
      } catch {
        setErr("Failed to fetch student details.");
      }
    },
    [branding]
  );

  useEffect(() => {
    if (routeStudentId) loadStudent(routeStudentId);
  }, [routeStudentId, loadStudent]);

  /* ── student search ── */
  const handleSearch = async (value) => {
    setStudentSearch(value);
    setErr("");
    if (value.length < 2) {
      setStudentResults([]);
      return;
    }
    setSearching(true);
    try {
      // A bare 10-digit string is a mobile number; anything else is a name query.
      const q = /^\d{10}$/.test(value)
        ? `/students/search?mobile=${value}`
        : `/students/search?text=${encodeURIComponent(value)}`;
      const { data } = await apiClient.get(q);
      setStudentResults(data.students || []);
    } catch {
      setStudentResults([]);
    }
    setSearching(false);
  };

  const selectStudent = async (stu) => {
    setStudentSearch("");
    setStudentResults([]);
    setErr("");
    setSelectedAssignment(null);
    setCourseAssignments([]);
    setSelectedInstallment(null);
    await loadStudent(stu._id);
  };

  const clearStudent = () => {
    setSelectedStudent(null);
    setStudentSearch("");
    setCourseAssignments([]);
    setSelectedAssignment(null);
    setSelectedInstallment(null);
    setFeePlan(null);
    setErr("");
    setForm((p) => ({ ...blankForm(), receivedBy: p.receivedBy, paidTo: p.paidTo }));
  };

  /* ── the selected enrollment's plan ── */
  const loadPlan = useCallback((caId) => {
    if (!caId) {
      setFeePlan(null);
      return;
    }
    setPlanLoading(true);
    apiClient
      .get(`/fees/plans/${caId}`)
      .then(({ data }) => setFeePlan(data.plan || null))
      .catch(() => setFeePlan(null))
      .finally(() => setPlanLoading(false));
  }, []);

  useEffect(() => {
    setSelectedInstallment(null);
    loadPlan(selectedAssignment?._id);
  }, [selectedAssignment, loadPlan]);

  /* ── derived ── */
  const totalFee = Number(selectedAssignment?.totalFees || selectedStudent?.totalFees || 0);
  const paidFee = Number(selectedAssignment?.paidFees || selectedStudent?.paidFees || 0);
  const pendingFee = Number(selectedAssignment?.pendingFees || selectedStudent?.pendingFees || 0);
  const isSelfPaced = selectedAssignment?.courseId?.courseType === "self-paced";

  const installments = useMemo(() => feePlan?.installments || [], [feePlan]);
  const planCount = installments.length;
  const nextDue =
    installments.find((i) => ["overdue", "due", "partial"].includes(i.status)) ||
    installments.find((i) => i.status === "upcoming") ||
    null;
  const currentEMI = selectedInstallment || nextDue;
  const pendingInst = installments.filter((i) => i.status !== "paid" && i.status !== "waived");
  const upcomingDues = pendingInst.reduce(
    (s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paidAmount) || 0), 0),
    0
  );
  const overdueInst = installments.filter((i) => i.status === "overdue");
  const overdueAmount = overdueInst.reduce(
    (s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paidAmount) || 0), 0),
    0
  );

  const amount = parseFloat(form.currentPayment) || 0;
  const dueAfter = Math.max((pendingFee || 0) - amount, 0);
  const onlineEnabled = !!paymentConfig?.enabled;
  const isRazorpay = form.paymentMode === RAZORPAY_MODE;
  const isLink = form.paymentMode === LINK_MODE;

  /* ── A self-paced course is collected in full, so the amount is not the
        admin's to type. It was READ-ONLY here and never filled in, which left
        the field permanently empty and the Collect button permanently rejected
        by `validate()` — self-paced fees could not be taken on this screen. ── */
  useEffect(() => {
    if (!isSelfPaced || pendingFee <= 0) return;
    setForm((p) => (p.currentPayment ? p : { ...p, currentPayment: String(pendingFee) }));
  }, [isSelfPaced, pendingFee]);

  const pickInstallment = (inst) => {
    if (inst.status === "paid" || inst.status === "waived") return;
    const bal = Math.max((Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0), 0);
    setSelectedInstallment(inst);
    setForm((p) => ({ ...p, currentPayment: String(bal) }));
  };

  const pickEnrollment = (ca) => {
    setSelectedAssignment(ca);
    setSelectedInstallment(null);
    setForm((f) => ({ ...f, currentPayment: "" }));
    setErr("");
  };

  const setChannel = (channel) => {
    setPayChannel(channel);
    setForm((p) => {
      const online = p.paymentMode === RAZORPAY_MODE || p.paymentMode === LINK_MODE;
      if (channel === "manual" && online) return { ...p, paymentMode: "UPI" };
      if (channel === "online" && !online) return { ...p, paymentMode: RAZORPAY_MODE };
      return p;
    });
  };

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* ── submit ── */
  const validate = () => {
    if (!selectedStudent) return "Select a student first.";
    if (!amount || amount <= 0) return "Enter a payment amount greater than zero.";
    // The server rejects an overpayment with the same rule; catching it here
    // saves a round trip and names the number the admin has to stay under.
    if (pendingFee > 0 && amount > pendingFee)
      return `That is more than the ${inr(pendingFee)} pending on this enrollment.`;
    if (payChannel === "online" && isLink && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(linkEmail.trim()))
      return "Enter a valid email address for the payment link.";
    if (payChannel === "manual") {
      if (!form.date) return "Pick the payment date.";
      // These two carry the asterisk, so they are checked. They are also the
      // whole audit trail on a cash payment — who took it, and into what.
      if (!form.paidTo) return "Choose the account this was paid into.";
      if (!form.receivedBy) return "Choose who received this payment.";
    }
    return "";
  };

  const createPaymentLink = async () => {
    setLinkSubmitting(true);
    setErr("");
    try {
      const { data } = await apiClient.post("/fee-payment-links", {
        studentId: selectedStudent._id,
        courseAssignmentId: selectedAssignment?._id || undefined,
        amount,
        email: linkEmail.trim(),
        description: form.comments || undefined,
        nextPaymentDate: form.nextPaymentDate || undefined,
      });
      setSentLink({ url: data.url, email: data.link?.sentToEmail || linkEmail.trim() });
      setLinkCopied(false);
    } catch (ex) {
      setErr(ex?.response?.data?.message || "Failed to create the payment link.");
    }
    setLinkSubmitting(false);
  };

  const payRazorpay = async () => {
    setRzpProcessing(true);
    setErr("");
    try {
      const { data: orderData } = await apiClient.post("/payments/create-order", {
        studentId: selectedStudent._id,
        amount,
        description: form.comments || "Fee Payment",
        courseAssignmentId: selectedAssignment?._id || undefined,
      });
      openCheckout(
        orderData,
        async (response) => {
          try {
            const { data: verifyData } = await apiClient.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: orderData.transactionId,
            });
            showToast("Online payment collected.");
            const feeRef = verifyData.transaction?.studentFeeId;
            if (feeRef) {
              setTimeout(
                () => navigate(`/students/${selectedStudent._id}/fees/view/${feeRef}`),
                800
              );
            }
          } catch {
            showToast("Payment received, but verification failed. Check the Fees list.", "error");
          }
          setRzpProcessing(false);
        },
        (error) => {
          showToast(error?.reason || "Payment cancelled or failed.", "error");
          setRzpProcessing(false);
        }
      );
    } catch (ex) {
      showToast(ex?.response?.data?.message || "Failed to create the order.", "error");
      setRzpProcessing(false);
    }
  };

  const handleCollect = async () => {
    setErr("");
    const invalid = validate();
    if (invalid) {
      setErr(invalid);
      return;
    }
    if (payChannel === "online") return isLink ? createPaymentLink() : payRazorpay();

    setSubmitting(true);
    try {
      // Plain JSON: `POST /fees/add` mounts no multer, and `UploadWithS3` has
      // already put the proof in storage and handed back its key — the page it
      // replaces still carried a `FormData` branch for a `File` that could not
      // reach it, and which the endpoint would have read as an empty body.
      const { data } = await apiClient.post("/fees/add", {
        ...form,
        studentId: selectedStudent._id,
        courseAssignmentId: selectedAssignment?._id || undefined,
        feePlanId: selectedInstallment ? feePlan?._id : undefined,
        installmentSeq: selectedInstallment?.seq ?? undefined,
      });
      showToast("Fee collected successfully.");
      const newFee = data?.transaction;
      if (newFee?._id) {
        setTimeout(() => navigate(`/students/${selectedStudent._id}/fees/view/${newFee._id}`), 700);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.message || "Failed to collect the fee.");
    }
    setSubmitting(false);
  };

  /* ⚠️ Resets the entry, never the context. Who is collecting and which account
     the money went to are this session's facts, not this payment's — the page
     this replaces snapshotted `initialForm` before either was resolved, so
     Reset silently blanked both. */
  const handleReset = () => {
    setForm((p) => ({ ...blankForm(), receivedBy: p.receivedBy, paidTo: p.paidTo }));
    setSelectedInstallment(null);
    setErr("");
  };

  const quickLink = () => {
    setChannel("online");
    setForm((p) => ({
      ...p,
      paymentMode: LINK_MODE,
      currentPayment:
        p.currentPayment ||
        (currentEMI
          ? String(Math.max((currentEMI.amount || 0) - (currentEMI.paidAmount || 0), 0))
          : pendingFee
            ? String(pendingFee)
            : ""),
    }));
  };

  const sendWhatsApp = async () => {
    if (!feePlan || !nextDue) return;
    setSendingWa(true);
    try {
      const { data } = await apiClient.post(
        `/fees/plans/${feePlan._id}/installments/${nextDue.seq}/remind`
      );
      if (data?.whatsappQueued) {
        showToast(
          data?.emailSent ? "Reminder sent by WhatsApp and email." : "Reminder sent by WhatsApp."
        );
      } else if (data?.emailSent) {
        showToast(
          "Reminder emailed. Add a WhatsApp fee-reminder template (WhatsApp → Automation) to send there too."
        );
      } else {
        showToast(
          "Couldn't deliver — configure a WhatsApp fee-reminder template, or add an email to this student.",
          "error"
        );
      }
    } catch (ex) {
      showToast(ex?.response?.data?.message || "Failed to send the reminder.", "error");
    }
    setSendingWa(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(sentLink.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      showToast("Couldn't copy — select the link and copy it manually.", "error");
    }
  };

  const paidToOptions = Array.from(new Set([branding?.brandName, form.paidTo].filter(Boolean)));
  const receivedByOptions = Array.from(new Set([form.receivedBy, ...staff].filter(Boolean)));

  const busy = submitting || rzpProcessing || rzpLoading || linkSubmitting;
  const collectLabel = isLink ? "Send Payment Link" : isRazorpay ? "Pay via Razorpay" : "Collect Fee";

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 has-form-actionbar">
      <PageHeader
        icon={CreditCard}
        title="Collect Fee"
        description="Record a payment against a student's enrollment"
        backButton
        onBack={() => navigate(-1)}
      />

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-[minmax(0,1fr)_340px]">
        {/* ══════════ MAIN ══════════ */}
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
          {/* ── Student ─────────────────────────────────────────────────── */}
          <AppCard overflowVisible>
            <AppCardHeader>
              <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                <UserRound size={16} aria-hidden="true" /> Student
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent overflowVisible>
              {selectedStudent ? (
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3.5 tw-py-3">
                  <UserAvatar name={selectedStudent.name} size="md" />
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                    <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content">
                      {selectedStudent.name}
                    </span>
                    <span className="tw-truncate tw-text-xs tw-text-content-subtle">
                      {selectedStudent.mobile}
                      {selectedStudent.email ? ` • ${selectedStudent.email}` : ""}
                    </span>
                  </div>
                  <IconButton
                    label="Choose a different student"
                    icon={X}
                    size="sm"
                    variant="ghost-destructive"
                    onClick={clearStudent}
                  />
                </div>
              ) : (
                <div className="tw-relative" ref={searchRef}>
                  <Input
                    size="lg"
                    value={studentSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search by name or 10-digit mobile number"
                    aria-label="Search for a student"
                    autoComplete="off"
                    leading={<Search size={15} aria-hidden="true" />}
                  />
                  {studentSearch.length >= 2 && (
                    <div className="tw-absolute tw-inset-x-0 tw-top-full tw-z-30 tw-mt-1.5 tw-max-h-80 tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-1.5 tw-shadow-popover">
                      {searching ? (
                        <Shimmer height={44} count={3} />
                      ) : studentResults.length === 0 ? (
                        <p className="tw-m-0 tw-px-3 tw-py-4 tw-text-center tw-text-[0.8125rem] tw-text-content-subtle">
                          No student matches “{studentSearch}”.
                        </p>
                      ) : (
                        studentResults.map((s) => (
                          <button
                            key={s._id}
                            type="button"
                            className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-sm tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left hover:tw-bg-surface-hover tw-focus-ring"
                            onClick={() => selectStudent(s)}
                          >
                            <UserAvatar name={s.name} size="sm" />
                            <span className="tw-flex tw-min-w-0 tw-flex-col">
                              <span className="tw-truncate tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                                {s.name}
                              </span>
                              <span className="tw-text-xs tw-text-content-subtle">{s.mobile}</span>
                            </span>
                            {Number(s.pendingFees) > 0 && (
                              <span className="tw-ml-auto tw-shrink-0 tw-text-xs tw-font-semibold tw-text-warning">
                                {inr(s.pendingFees)} due
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>

          {/* ── Enrollment ──────────────────────────────────────────────── */}
          {selectedStudent && courseAssignments.length > 0 && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>Enrollment</AppCardTitle>
                <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                  The payment is recorded against this course, and its pending balance is the
                  ceiling for the amount.
                </p>
              </AppCardHeader>
              <AppCardContent>
                <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 md:tw-grid-cols-2">
                  {courseAssignments.map((ca) => {
                    const selected = selectedAssignment?._id === ca._id;
                    const pending = Number(ca.pendingFees || 0);
                    const paid = Number(ca.paidFees || 0);
                    return (
                      <PanelButton
                        key={ca._id}
                        aria-pressed={selected}
                        className={cn(
                          "tw-flex-col tw-items-stretch tw-gap-2",
                          selected && SELECTED_PANEL
                        )}
                        onClick={() => pickEnrollment(ca)}
                      >
                        <span className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                          <span className="tw-flex tw-min-w-0 tw-flex-col">
                            <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content">
                              {ca.courseId?.name || "Course"}
                            </span>
                            <span className="tw-truncate tw-text-xs tw-text-content-subtle">
                              {ca.batchId?.name || "No batch"}
                            </span>
                          </span>
                          <StatusBadge
                            status={balanceStatus(paid, pending)}
                            domain="feeBalance"
                          />
                        </span>
                        <span className="tw-grid tw-grid-cols-3 tw-gap-2">
                          <Figure label="Total" value={inr(ca.totalFees)} />
                          <Figure label="Paid" value={inr(paid)} tone="tw-text-success" />
                          <Figure
                            label="Pending"
                            value={inr(pending)}
                            tone={pending > 0 ? "tw-text-warning" : undefined}
                          />
                        </span>
                      </PanelButton>
                    );
                  })}
                </div>
              </AppCardContent>
            </AppCard>
          )}

          {/* ── Fee plan ────────────────────────────────────────────────── */}
          {selectedStudent && selectedAssignment && (
            <AppCard>
              {/* `--row` is the card's own modifier for a header with an action.
                  Reaching for `tw-flex-row` instead would be a utility racing
                  `.app-card__header`'s `flex-direction: column` on equal
                  specificity, settled by which sheet the bundler emitted last. */}
              <AppCardHeader className="app-card__header--row">
                <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                  Fee Plan
                  {planCount > 0 && (
                    <span className="tw-rounded-full tw-bg-surface-sunken tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-content-muted">
                      {planCount} instalments
                    </span>
                  )}
                </AppCardTitle>
                <AppButton size="sm" variant="outline" onClick={() => setShowPlanModal(true)}>
                  <FileText size={16} /> {feePlan ? "Edit plan" : "Set up plan"}
                </AppButton>
              </AppCardHeader>
              <AppCardContent>
                {planLoading ? (
                  <Shimmer height={92} />
                ) : !feePlan ? (
                  <EmptyState
                    icon={Calendar}
                    title="No instalment plan"
                    description="Collect the full balance below, or split it into EMIs first."
                    action={
                      <AppButton size="sm" variant="outline" onClick={() => setShowPlanModal(true)}>
                        Set up a plan
                      </AppButton>
                    }
                  />
                ) : (
                  <>
                    <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-2">
                      {installments.map((inst) => {
                        const v = emiView(inst);
                        const selected = currentEMI?.seq === inst.seq;
                        return (
                          <PanelButton
                            key={inst.seq}
                            aria-pressed={selected}
                            disabled={v.settled}
                            className={cn(
                              "tw-w-56 tw-shrink-0 tw-flex-col tw-items-stretch tw-gap-1.5",
                              selected && SELECTED_PANEL
                            )}
                            onClick={() => pickInstallment(inst)}
                          >
                            <span className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                              <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                                {inst.label || `EMI ${inst.seq}`}
                              </span>
                              <StatusBadge status={v.status} domain="feeInstallment" />
                            </span>
                            <span className="tw-text-lg tw-font-bold tw-leading-tight tw-text-content">
                              {inr(inst.amount)}
                            </span>
                            {!v.settled && v.paidAmount > 0 && (
                              <span className="tw-text-xs tw-font-semibold tw-text-warning">
                                {inr(v.paidAmount)} paid · {inr(v.balance)} left
                              </span>
                            )}
                            <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
                              {v.settled ? (
                                <>
                                  <CheckCircle2 size={12} className="tw-text-success" />
                                  {inst.status === "waived"
                                    ? "Waived"
                                    : `Paid ${fmtDate(inst.paidAt || inst.dueDate)}`}
                                </>
                              ) : (
                                <>
                                  <Calendar size={12} />
                                  {fmtDate(inst.dueDate)}
                                  <span
                                    className={cn(
                                      "tw-font-semibold",
                                      v.late ? "tw-text-danger" : "tw-text-content-subtle"
                                    )}
                                  >
                                    · {v.relative}
                                  </span>
                                </>
                              )}
                            </span>
                          </PanelButton>
                        );
                      })}
                    </div>
                    <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
                      Pick an instalment to bill exactly its outstanding balance.
                    </p>
                  </>
                )}
              </AppCardContent>
            </AppCard>
          )}

          {/* ── Payment ─────────────────────────────────────────────────── */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
                <CreditCard size={16} aria-hidden="true" /> Payment
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="tw-flex tw-flex-col tw-gap-4">
                {/* Channel */}
                {/* ⚠️ Not a <fieldset>/<legend>: Bootstrap Reboot floats a legend
                    and gives it `calc(1.275rem + .3vw)`, so it would need
                    unstyling before it could be styled. A labelled group is the
                    right ARIA for a set of toggle buttons either way. */}
                <div role="group" aria-labelledby={channelLabelId}>
                  <span
                    id={channelLabelId}
                    className="tw-mb-1.5 tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content"
                  >
                    How is it being paid?
                  </span>
                  <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2">
                    <PanelButton
                      aria-pressed={payChannel === "manual"}
                      className={cn(payChannel === "manual" && SELECTED_PANEL)}
                      onClick={() => setChannel("manual")}
                    >
                      <IndianRupee size={16} className="tw-shrink-0" aria-hidden="true" />
                      <span className="tw-min-w-0">
                        <span className="tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                          Manual payment
                        </span>
                        <span className="tw-block tw-text-xs tw-text-content-muted">
                          Cash, UPI or a bank transfer you have already received
                        </span>
                      </span>
                    </PanelButton>
                    <PanelButton
                      aria-pressed={payChannel === "online"}
                      disabled={!onlineEnabled}
                      className={cn(payChannel === "online" && SELECTED_PANEL)}
                      onClick={() => setChannel("online")}
                    >
                      <Wifi size={16} className="tw-shrink-0" aria-hidden="true" />
                      <span className="tw-min-w-0">
                        <span className="tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                          Online payment
                        </span>
                        <span className="tw-block tw-text-xs tw-text-content-muted">
                          {onlineEnabled
                            ? "Charge the card now, or email a payment link"
                            : "Connect a payment gateway in Settings to use this"}
                        </span>
                      </span>
                    </PanelButton>
                  </div>
                </div>

                {/* Money */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
                  <FormField
                    label="Amount"
                    required
                    hint={
                      isSelfPaced
                        ? "Self-paced courses are collected in full."
                        : pendingFee > 0
                          ? `${inr(pendingFee)} pending`
                          : undefined
                    }
                  >
                    <Input
                      size="lg"
                      type="number"
                      name="currentPayment"
                      value={form.currentPayment}
                      onChange={onChange}
                      min={1}
                      max={pendingFee || undefined}
                      placeholder="0"
                      readOnly={isSelfPaced}
                      leading={<IndianRupee size={14} aria-hidden="true" />}
                    />
                  </FormField>

                  {/* `disabled`, not `readOnly` + a background class: `cn()` does
                      not merge, so an overriding `tw-bg-*` would race the
                      control's own and be settled by stylesheet order. The
                      primitive already owns how an inert field looks, and the
                      same number is in the Summary card for anyone who cannot
                      reach a disabled input. */}
                  <FormField label="Balance after this" hint="Recalculated as you type.">
                    <Input size="lg" disabled value={inr(dueAfter)} />
                  </FormField>

                  {payChannel === "manual" && (
                    <>
                      <FormField label="Payment date" required>
                        <Input
                          size="lg"
                          type="date"
                          name="date"
                          max={todayISO()}
                          value={form.date}
                          onChange={onChange}
                        />
                      </FormField>

                      <FormField label="Method" required>
                        <NativeSelect
                          size="lg"
                          name="paymentMode"
                          value={MANUAL_MODES.includes(form.paymentMode) ? form.paymentMode : "UPI"}
                          onChange={onChange}
                          options={MANUAL_MODES.map((m) => ({ value: m, label: m }))}
                        />
                      </FormField>

                      <FormField label="Paid to" required>
                        <NativeSelect size="lg" name="paidTo" value={form.paidTo} onChange={onChange}>
                          <option value="">Select account</option>
                          {paidToOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </NativeSelect>
                      </FormField>

                      <FormField label="Received by" required>
                        <NativeSelect
                          size="lg"
                          name="receivedBy"
                          value={form.receivedBy}
                          onChange={onChange}
                        >
                          <option value="">Select staff</option>
                          {receivedByOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </NativeSelect>
                      </FormField>

                      <FormField
                        label="Reference / transaction id"
                        hint={form.paymentMode === "Cash" ? "Not needed for cash." : undefined}
                      >
                        <Input
                          size="lg"
                          name="referenceNumber"
                          value={form.referenceNumber}
                          onChange={onChange}
                          placeholder="UPI1234567890"
                          disabled={form.paymentMode === "Cash"}
                        />
                      </FormField>
                    </>
                  )}
                </div>

                {/* Online method */}
                {payChannel === "online" && (
                  <div role="group" aria-labelledby={onlineLabelId}>
                    <span
                      id={onlineLabelId}
                      className="tw-mb-1.5 tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content"
                    >
                      Online method
                    </span>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-2.5 sm:tw-grid-cols-2">
                      <PanelButton
                        aria-pressed={isRazorpay}
                        className={cn(isRazorpay && SELECTED_PANEL)}
                        onClick={() => setForm((p) => ({ ...p, paymentMode: RAZORPAY_MODE }))}
                      >
                        <Wifi size={16} className="tw-shrink-0" aria-hidden="true" />
                        <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                          Take the payment now
                        </span>
                      </PanelButton>
                      <PanelButton
                        aria-pressed={isLink}
                        className={cn(isLink && SELECTED_PANEL)}
                        onClick={() => setForm((p) => ({ ...p, paymentMode: LINK_MODE }))}
                      >
                        <Mail size={16} className="tw-shrink-0" aria-hidden="true" />
                        <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                          Email a payment link
                        </span>
                      </PanelButton>
                    </div>
                  </div>
                )}

                {payChannel === "online" && isLink && (
                  <FormField label="Send the link to" required>
                    <Input
                      size="lg"
                      type="email"
                      value={linkEmail}
                      onChange={(e) => setLinkEmail(e.target.value)}
                      placeholder="student@example.com"
                      leading={<Mail size={15} aria-hidden="true" />}
                    />
                  </FormField>
                )}

                {/* Proof + notes */}
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                  {payChannel === "manual" && (
                    // ⚠️ Not wrapped in a `FormField`: that generates an id and
                    // clones its child to inject it, and this control is a
                    // composite with no single input to receive it — the
                    // label's `htmlFor` would point at nothing. It carries its
                    // own label, the way `ui/FileUpload` does.
                    <UploadWithS3
                      label="Payment proof"
                      hint={
                        selectedStudent
                          ? "Drop, paste or browse for the receipt. Click it afterwards to zoom."
                          : "Select a student before attaching a receipt."
                      }
                      s3KeyPrefix={
                        selectedStudent ? `students/fee-screenshots/${selectedStudent._id}` : ""
                      }
                      accept="image/*"
                      disabled={!selectedStudent}
                      value={form.refImage}
                      onUpload={(key) => setForm((p) => ({ ...p, refImage: key || null }))}
                      onDelete={() => setForm((p) => ({ ...p, refImage: null }))}
                    />
                  )}
                  <FormField label="Notes" className={payChannel === "online" ? "sm:tw-col-span-2" : ""}>
                    <Textarea
                      size="lg"
                      name="comments"
                      rows={3}
                      value={form.comments}
                      onChange={onChange}
                      placeholder="Anything worth recording about this payment"
                    />
                  </FormField>
                </div>

                {rzpProcessing && (
                  <Callout tone="info">Waiting for the payment gateway to finish…</Callout>
                )}
                {err && (
                  <Callout tone="danger" role="alert">
                    {err}
                  </Callout>
                )}
              </div>
            </AppCardContent>
          </AppCard>
        </div>

        {/* ══════════ SIDEBAR ══════════ */}
        <aside className="tw-flex tw-flex-col tw-gap-4 xl:tw-sticky xl:tw-top-20 xl:tw-self-start">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Summary</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              {!selectedStudent ? (
                <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-subtle">
                  Pick a student to see their balance here.
                </p>
              ) : (
                <>
                  <div className="tw-mb-3 tw-flex tw-items-center tw-gap-3">
                    <UserAvatar name={selectedStudent.name} size="md" />
                    <div className="tw-flex tw-min-w-0 tw-flex-col">
                      <span className="tw-truncate tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                        {selectedStudent.name}
                      </span>
                      <span className="tw-truncate tw-text-xs tw-text-content-subtle">
                        {selectedStudent.mobile}
                      </span>
                    </div>
                  </div>
                  <DetailSection>
                    <DetailRow
                      label="Course"
                      value={
                        selectedAssignment?.courseId?.name || selectedStudent.course?.name || "—"
                      }
                    />
                    <DetailRow
                      label="Batch"
                      value={selectedAssignment?.batchId?.name || selectedStudent.batch?.name || "—"}
                    />
                    <DetailRow label="Total fees" value={inr(totalFee)} />
                    <DetailRow
                      label="Paid"
                      value={<span className="tw-text-success">{inr(paidFee)}</span>}
                    />
                    <DetailRow
                      label="Pending"
                      value={<span className="tw-text-warning">{inr(pendingFee)}</span>}
                    />
                    {feePlan && (
                      <DetailRow
                        label={`Upcoming (${pendingInst.length})`}
                        value={inr(upcomingDues)}
                      />
                    )}
                    {feePlan && overdueInst.length > 0 && (
                      <DetailRow
                        label={`Overdue (${overdueInst.length})`}
                        value={
                          <span className="tw-font-semibold tw-text-danger">
                            {inr(overdueAmount)}
                          </span>
                        }
                      />
                    )}
                  </DetailSection>
                </>
              )}
            </AppCardContent>
          </AppCard>

          {selectedStudent && feePlan && currentEMI && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  {selectedInstallment ? "Billing this instalment" : "Next instalment"}
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <DetailSection>
                  <DetailRow label="Instalment" value={`${currentEMI.seq} of ${planCount}`} />
                  <DetailRow label="Due date" value={fmtDate(currentEMI.dueDate)} />
                  <DetailRow label="Amount" value={inr(currentEMI.amount)} />
                  <DetailRow
                    label="Paid"
                    value={<span className="tw-text-success">{inr(currentEMI.paidAmount)}</span>}
                  />
                  <DetailRow
                    label="Outstanding"
                    value={
                      <span className="tw-font-semibold tw-text-danger">
                        {inr(Math.max((currentEMI.amount || 0) - (currentEMI.paidAmount || 0), 0))}
                      </span>
                    }
                  />
                </DetailSection>
              </AppCardContent>
            </AppCard>
          )}

          {selectedStudent && selectedAssignment && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>Quick actions</AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={!onlineEnabled}
                    onClick={quickLink}
                  >
                    <Link2 size={16} /> Generate a payment link
                  </AppButton>
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={!feePlan || !nextDue || sendingWa}
                    onClick={sendWhatsApp}
                  >
                    <MessageCircle size={16} />
                    {sendingWa ? "Sending…" : "Send a reminder"}
                  </AppButton>
                  <AppButton variant="outline" size="sm" onClick={() => setShowPlanModal(true)}>
                    <FileText size={16} /> {feePlan ? "View the fee plan" : "Set up a fee plan"}
                  </AppButton>
                </div>
              </AppCardContent>
            </AppCard>
          )}
        </aside>
      </div>

      <FormActionBar
        info={
          selectedStudent && amount > 0
            ? `Collecting ${inr(amount)} from ${selectedStudent.name}`
            : null
        }
      >
        <AppButton variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </AppButton>
        <ResetButton
          onReset={handleReset}
          disabled={busy}
          message="This clears the amount, the notes and the proof you have attached. The student and enrollment stay selected."
        />
        <AppButton variant="default" onClick={handleCollect} disabled={!selectedStudent || busy}>
          <CreditCard size={16} />
          {busy ? "Processing…" : collectLabel}
        </AppButton>
      </FormActionBar>

      <FeePlanModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        courseAssignment={selectedAssignment}
        existingPlan={feePlan}
        onSaved={() => {
          setShowPlanModal(false);
          loadPlan(selectedAssignment?._id);
        }}
      />

      <AppModal
        show={!!sentLink}
        onHide={() => setSentLink(null)}
        title="Payment link sent"
        icon={<CheckCircle2 size={18} />}
        size="md"
        footer={
          <AppButton
            onClick={() => {
              setSentLink(null);
              clearStudent();
            }}
          >
            Done
          </AppButton>
        }
      >
        {sentLink && (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-text-sm tw-text-content-muted">
            <p className="tw-m-0">
              A secure link was emailed to <strong className="tw-text-content">{sentLink.email}</strong>.
              You can share this copy as well.
            </p>
            <div className="tw-flex tw-gap-2">
              <Input readOnly value={sentLink.url} onFocus={(e) => e.target.select()} />
              <AppButton variant="outline" onClick={copyLink}>
                {linkCopied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                {linkCopied ? "Copied" : "Copy"}
              </AppButton>
            </div>
            <p className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
              <Info size={13} aria-hidden="true" /> Valid for 7 days. The balance is re-checked when
              the student pays, so an amount already settled cannot be charged twice.
            </p>
          </div>
        )}
      </AppModal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/** One figure in an enrollment panel. Inside a `<button>`, so every node is a
 *  `<span>` — a `<div>` here is invalid content for the button that wraps it. */
const Figure = ({ label, value, tone }) => (
  <span className="tw-flex tw-flex-col">
    <span className={cn("tw-text-[0.8125rem] tw-font-bold tw-text-content", tone)}>{value}</span>
    <span className="tw-text-xs tw-text-content-subtle">{label}</span>
  </span>
);

export default CollectFeePage;
